import { useMutation } from '@apollo/client/react';
import {
  CancelPreBookingDocument,
  type CompletePreBookingFragment,
  PreBookingState,
} from '@drycut/graphql';
import { Navigation } from '@drycut/navigation';
import {
  ActionButtons,
  Button,
  Heading,
  Link,
  MobileNavbar,
  Para,
  Stack,
} from '@drycut/ui-components';
import { formatDateTime } from '@drycut/ui-format-utils';
import { DateTime } from 'luxon';
import { useState } from 'react';

import GrayBox from './GrayBox';
import { LocationLink } from './LocationLink';
import PreBookingCancelConfirm from './PreBookingCancelConfirm';

type Props = {
  preBooking: CompletePreBookingFragment;
};

export function PreBookingDetails({ preBooking }: Props): React.ReactElement {
  const bookTime = DateTime.fromISO(preBooking.bookTime);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  const [cancelPreBooking, { loading }] = useMutation(CancelPreBookingDocument, {
    variables: {
      input: {
        preBookingId: preBooking.id,
      },
    },
  });

  const toggleConfirm = () => {
    setConfirmOpen(!confirmOpen);
  };

  const handleCancelConfirm = () => {
    cancelPreBooking();
    setConfirmOpen(false);
  };

  if (preBooking?.state === PreBookingState.Cancelled) {
    return (
      <Stack gap={5} className="h-full text-center font-aribau lg:gap-9" id="pre-booking-details">
        <MobileNavbar title="Diese Reservierung..." />

        <Heading variant="h2" className="hidden lg:block">
          Diese Reservierung...
        </Heading>

        <Stack gap={3}>
          <Para pretty>
            ...wurde storniert. Du bekommst eine sms mit der Bestätigung und einem Code für eine
            neue Buchung. Bis bald!
          </Para>

          <Para pretty>
            Für Fragen, bitte sende uns eine Email an{' '}
            <Link href="mailto:hey@drycut.ch">hey@drycut.ch</Link>
          </Para>
        </Stack>

        <ActionButtons>
          <Button as="a" href={Navigation.CHECKOUT_URL} role="link">
            Neue Buchung
          </Button>
        </ActionButtons>
      </Stack>
    );
  }

  return (
    <>
      <Stack gap={5} className="h-full text-center font-aribau lg:gap-9" id="pre-booking-details">
        <MobileNavbar title="Deine Reservierung" />

        <Stack gap={5}>
          <Heading variant="h2" className="hidden lg:block">
            Deine Reservierung
          </Heading>

          <Para pretty>
            Wir haben Deine Reservierung erhalten und Dich eingetragen für einen Cut am:
          </Para>
        </Stack>

        <GrayBox className="gap-2">
          <Para size="xl">
            {formatDateTime(preBooking?.bookTime, {
              weekday: 'long',
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </Para>

          <Stack direction="row" gap={1} className="justify-center">
            <Para as="span" size="xl">
              ca.
            </Para>

            <Para as="span" size="xl" weight="medium">
              {bookTime.toFormat('HH:mm')}
            </Para>

            <Para as="span" size="xl">
              (+/- 10 min)
            </Para>
          </Stack>
        </GrayBox>

        <Stack gap={2} className="items-center">
          <Para>im</Para>

          <Para size="2xl" weight="bold">
            DRYCUT {preBooking?.store.name}
          </Para>

          {preBooking?.store.location.mapUrl && (
            <LocationLink mapUrl={preBooking.store.location.mapUrl} />
          )}
          <Button size="small" color="secondary" onClick={toggleConfirm} loading={loading}>
            Buchung stornieren
          </Button>
        </Stack>

        <Stack>
          <Para pretty size="sm">
            Du erhältst vor dem Cut eine separate sms mit den genauen Buchungsinformationen. Wir
            freuen uns auf Dich!
          </Para>
        </Stack>

        <ActionButtons>
          <Button as="a" href={Navigation.HOME_URL} role="link">
            Schliessen
          </Button>
        </ActionButtons>
      </Stack>

      <PreBookingCancelConfirm
        open={confirmOpen}
        onClose={toggleConfirm}
        onConfirm={handleCancelConfirm}
      />
    </>
  );
}
